import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TopProductItem, DashboardFilters } from "@/types/dashboard";
import { Mask } from "@/utils/mask";
import { Package, Crown, Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TopProductsCardProps {
  data: TopProductItem[];
  period?: DashboardFilters["period"];
}

const periodLabels: Record<string, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  week: "Semana",
  month: "Mês",
  custom: "Período",
};

export function TopProductsCard({ data, period = "month" }: TopProductsCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getPositionStyle = (index: number) => {
    if (index === 0) return "bg-yellow-400 text-yellow-900";
    if (index === 1) return "bg-gray-300 text-gray-700";
    if (index === 2) return "bg-amber-400 text-amber-900";
    return "bg-white/30 text-indigo-100";
  };

  const getModalPositionStyle = (index: number) => {
    if (index === 0) return "bg-gradient-to-br from-yellow-400 to-yellow-500 text-yellow-900 shadow-lg shadow-yellow-500/30";
    if (index === 1) return "bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700 shadow-lg shadow-gray-400/30";
    if (index === 2) return "bg-gradient-to-br from-amber-400 to-amber-500 text-amber-900 shadow-lg shadow-amber-500/30";
    return "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300";
  };

  const periodLabel = periodLabels[period || "month"] || "Mês";
  const displayProducts = data.slice(0, 5);
  const totalProducts = data.length;

  const renderProductItem = (product: TopProductItem, index: number) => (
    <div
      key={product.id || index}
      className="flex items-center gap-3 p-2.5 rounded-xl bg-white/10 backdrop-blur"
    >
      <span
        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${getPositionStyle(index)}`}
      >
        {index === 0 ? <Crown className="h-3 w-3" /> : index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight truncate">
          {product.product_name}
        </p>
        <p className="text-[10px] text-indigo-200">
          {product.total_quantity} vendidos
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-white">
          {Mask.formatCurrency(product.total_value)}
        </p>
      </div>
    </div>
  );

  const renderModalProductItem = (product: TopProductItem, index: number) => (
    <div
      key={product.id || index}
      className="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
    >
      <span
        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${getModalPositionStyle(index)}`}
      >
        {index === 0 ? <Crown className="h-4 w-4" /> : index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 dark:text-white">
          {product.product_name}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {product.total_quantity} unidades vendidas
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
          {Mask.formatCurrency(product.total_value)}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-700 text-white">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white/20 backdrop-blur rounded-lg">
                <Package className="h-4 w-4" />
              </div>
              <span className="font-medium">Top Produtos</span>
            </div>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{periodLabel}</span>
          </div>

          {data.length > 0 ? (
            <div className="space-y-2">
              {displayProducts.map((product, index) => renderProductItem(product, index))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-3">
                <Package className="h-6 w-6 text-indigo-200" />
              </div>
              <p className="text-indigo-200">Nenhum produto vendido</p>
            </div>
          )}

          {totalProducts > 5 && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full text-xs text-white/80 hover:text-white text-center mt-3 pt-3 border-t border-white/20 hover:underline flex items-center justify-center gap-1"
            >
              <Eye className="h-3 w-3" />
              Ver todos os {totalProducts} produtos
            </button>
          )}
        </CardContent>
      </Card>

      {/* Modal com todos os produtos */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 overflow-hidden">
          {/* Header com gradiente */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-white text-xl">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Package className="h-6 w-6" />
                </div>
                Top Produtos - {periodLabel}
              </DialogTitle>
            </DialogHeader>
            <p className="text-indigo-200 mt-2 text-sm">
              {totalProducts} produtos vendidos no período
            </p>
          </div>

          {/* Lista de produtos */}
          <ScrollArea className="h-[55vh] p-4">
            <div className="space-y-3">
              {data.map((product, index) => renderModalProductItem(product, index))}
            </div>
          </ScrollArea>

          {/* Footer com resumo */}
          <div className="border-t bg-gray-50 dark:bg-gray-800/50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>{totalProducts}</strong> produto{totalProducts !== 1 ? "s" : ""} no ranking
                </span>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {periodLabel}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
